import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ViewEncapsulation,
  inject,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as L from 'leaflet';
import 'leaflet-draw';
import './leaflet-markercluster-shim';
import 'leaflet.markercluster';
import { KeycloakService } from 'keycloak-angular';
import { OgcFeatureApiService, OgcFeature, OgcFeatureCollection } from '../../../services/ogc-feature-api.service';
import { ToasterService } from '../../../../services/toaster.service';

export interface DrawnGeometry {
  type: 'polygon' | 'circle' | 'rectangle';
  bbox: [number, number, number, number];
  coordinates?: [number, number][];
  center?: [number, number];
  radius?: number;
}

@Component({
  selector: 'app-ogc-feature-data',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ogc-feature-data.component.html',
  styleUrls: ['./ogc-feature-data.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OgcFeatureDataComponent implements AfterViewInit, OnDestroy {
  @Input() resourceItemData: { id?: string; label?: string; name?: string; ogcServerBaseUrl?: string } | null = null;

  @Output() closeRequested = new EventEmitter<void>();
  @Output() geometryDrawn = new EventEmitter<DrawnGeometry>();

  @ViewChild('featureMap') featureMapRef!: ElementRef<HTMLDivElement>;

  private keycloak = inject(KeycloakService);
  private ogcApi = inject(OgcFeatureApiService);
  private toasterService = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  get title(): string {
    const d = this.resourceItemData;
    return (d?.label ?? d?.name ?? 'OGC Feature') || 'OGC Feature';
  }

  map: L.Map | null = null;
  markerClusterGroup: any = null;
  polygonsLayer = new (L as any).FeatureGroup();
  drawnItems = new (L as any).FeatureGroup();
  drawControl: any = null;
  polygonHandler: any = null;
  rectangleHandler: any = null;
  circleHandler: any = null;
  activeDrawMode: 'polygon' | 'rectangle' | 'circle' | null = null;
  currentGeometry: DrawnGeometry | null = null;

  /** Apply Selection: fetch state */
  isApplying = false;
  applyError: string | null = null;
  featureCollection: OgcFeatureCollection | null = null;

  selectedFeature: OgcFeature | null = null;
  copiedToClipboard = false;

  private resizeObserver: ResizeObserver | null = null;
  private featureMarkers: any[] = [];

  get features(): OgcFeature[] {
    return this.featureCollection?.features ?? [];
  }

  get hasApiResponse(): boolean {
    return this.featureCollection !== null;
  }

  get selectedBbox(): { north: number; south: number; east: number; west: number } | null {
    if (!this.currentGeometry?.bbox || this.currentGeometry.bbox.length !== 4) return null;
    const [west, south, east, north] = this.currentGeometry.bbox;
    return { north, south, east, west };
  }

  ngAfterViewInit(): void {
    const el = this.featureMapRef?.nativeElement;
    if (!el) return;
    setTimeout(() => this.initMap(el), 0);
  }

  ngOnDestroy(): void {
    this.clearFeatureMarkers();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  /** India bounding box: [south, west], [north, east] - restricts map view to India */
  private static readonly INDIA_BOUNDS = [[6.5, 67], [36, 98]] as [[number, number], [number, number]];
  /** Default map center and zoom for reset */
  private static readonly DEFAULT_CENTER: [number, number] = [20.5937, 78.9629];
  private static readonly DEFAULT_ZOOM = 4;

  private initMap(el: HTMLDivElement): void {
    const Lx = L as any;
    const indiaBounds = Lx.latLngBounds(OgcFeatureDataComponent.INDIA_BOUNDS);

    this.map = Lx.map(el, {
      center: Lx.latLng(...OgcFeatureDataComponent.DEFAULT_CENTER),
      zoom: OgcFeatureDataComponent.DEFAULT_ZOOM,
      zoomControl: false,
      maxBounds: indiaBounds,
      maxBoundsViscosity: 1,
      minZoom: 3,
      maxZoom: 18,
    });

    Lx.control.zoom({ position: 'bottomright' }).addTo(this.map);

    Lx.tileLayer(
      'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
      { maxZoom: 19, attribution: '&copy; OpenStreetMap, &copy; CARTO' },
    ).addTo(this.map);

    this.markerClusterGroup = this.createMarkerClusterGroup();
    this.markerClusterGroup.addTo(this.map);
    this.polygonsLayer.addTo(this.map);
    this.drawnItems.addTo(this.map);

    this.setupDrawHandlers();

    [100, 300, 600].forEach((delay, idx) => {
      setTimeout(() => {
        if (!this.map) return;
        this.map.invalidateSize(true);
        if (idx === 2) {
          this.addDrawControl();
        }
      }, delay);
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.map?.invalidateSize(true);
      });
      this.resizeObserver.observe(el);
    }
  }

  private createMarkerClusterGroup(): any {
    const Lx = L as any;
    const MCG = Lx.MarkerClusterGroup ?? Lx.markerClusterGroup;
    if (!MCG) return new Lx.FeatureGroup();

    return new MCG({
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount();
        return Lx.divIcon({
          html: `<div class="marker-cluster-custom"><span>${count}</span></div>`,
          className: 'marker-cluster-custom-wrap',
          iconSize: Lx.point(40, 40),
        });
      },
    });
  }

  private addDrawControl(): void {
    if (!this.map) return;
    const Lx = L as any;
    if (!Lx.Draw?.Polygon) return;

    const shapeOptions = { color: '#0d5c42', weight: 3, fillOpacity: 0.25 };
    this.polygonHandler = new Lx.Draw.Polygon(this.map, {
      allowIntersection: false,
      showArea: true,
      drawError: { color: '#e1e100', message: 'Shape edges cannot cross!' },
      shapeOptions,
    });
    this.rectangleHandler = new Lx.Draw.Rectangle(this.map, { shapeOptions });
    this.circleHandler = new Lx.Draw.Circle(this.map, {
      showArea: true,
      shapeOptions,
    });

    [this.polygonHandler, this.rectangleHandler, this.circleHandler].forEach((h) => {
      h.on('disabled', () => {
        this.ngZone.run(() => {
          this.activeDrawMode = null;
          this.cdr.detectChanges();
        });
      });
    });
    this.drawControl = true;
  }

  startDrawPolygon(): void {
    this.setDrawMode('polygon');
  }
  startDrawRectangle(): void {
    this.setDrawMode('rectangle');
  }
  startDrawCircle(): void {
    this.setDrawMode('circle');
  }

  private setDrawMode(mode: 'polygon' | 'rectangle' | 'circle'): void {
    if (!this.map) return;
    this.disableDrawMode();
    const handler =
      mode === 'polygon'
        ? this.polygonHandler
        : mode === 'rectangle'
          ? this.rectangleHandler
          : this.circleHandler;
    if (handler) {
      handler.enable();
      this.activeDrawMode = mode;
      this.cdr.detectChanges();
    }
  }

  private disableDrawMode(): void {
    [this.polygonHandler, this.rectangleHandler, this.circleHandler].forEach((h) => {
      if (h) h.disable();
    });
    this.activeDrawMode = null;
  }

  private setupDrawHandlers(): void {
    if (!this.map) return;
    const Draw = (L as any).Draw;

    this.map.on(Draw.Event.CREATED, (event: any) => {
      this.ngZone.run(() => {
        this.drawnItems.clearLayers();
        this.drawnItems.addLayer(event.layer);
        this.currentGeometry = this.extractGeometry(event.layerType, event.layer);
        this.applyError = null;
        this.cdr.detectChanges();
        this.applySelection();
      });
    });

    this.map.on(Draw.Event.EDITED, () => {
      this.ngZone.run(() => {
        this.drawnItems.eachLayer((layer: any) => {
          const type = layer instanceof (L as any).Circle ? 'circle' : 'polygon';
          this.currentGeometry = this.extractGeometry(type, layer);
          this.applyError = null;
        });
        this.cdr.detectChanges();
        this.applySelection();
      });
    });

    this.map.on(Draw.Event.DELETED, () => {
      this.ngZone.run(() => {
        this.currentGeometry = null;
        this.applyError = null;
        this.cdr.detectChanges();
      });
    });
  }

  private extractGeometry(layerType: string, layer: any): DrawnGeometry | null {
    if (layerType === 'circle') {
      const center = layer.getLatLng();
      const radius = layer.getRadius();
      const bounds = layer.getBounds();
      return {
        type: 'circle',
        bbox: [
          bounds.getSouthWest().lng,
          bounds.getSouthWest().lat,
          bounds.getNorthEast().lng,
          bounds.getNorthEast().lat,
        ],
        center: [center.lat, center.lng],
        radius,
      };
    }

    if (layerType === 'polygon' || layerType === 'rectangle') {
      const latlngs: any[] = layer.getLatLngs ? (layer.getLatLngs()[0] ?? layer.getLatLngs()) : [];
      const bounds = layer.getBounds();
      return {
        type: (layerType === 'rectangle' ? 'rectangle' : 'polygon') as 'polygon' | 'rectangle',
        bbox: [
          bounds.getSouthWest().lng,
          bounds.getSouthWest().lat,
          bounds.getNorthEast().lng,
          bounds.getNorthEast().lat,
        ],
        coordinates: latlngs.length ? latlngs.map((ll: any) => [ll.lat, ll.lng]) : undefined,
      };
    }

    return null;
  }

  async applySelection(): Promise<void> {
    if (!this.currentGeometry || !this.resourceItemData?.id) return;
    this.isApplying = true;
    this.applyError = null;
    this.featureCollection = null;
    this.clearFeatureMarkers();
    this.cdr.markForCheck();

    const serverBaseUrl = this.resourceItemData.ogcServerBaseUrl;

    try {
      const token = await this.keycloak.getToken().catch(() => undefined);

      // Direct bbox query using OGC collections/items endpoint:
      // <catdomainname>/collections/<itemId>/items?bbox=<dynamicvalue>&bbox-crs=...
      let result: OgcFeatureCollection | null = null;

      if (token) {
        try {
          result = await this.ogcApi.fetchItemsByBbox(
            this.resourceItemData.id,
            this.currentGeometry.bbox,
            token,
            serverBaseUrl
          );
        } catch (authErr: any) {
          if (authErr?.status === 401 || authErr?.status === 403) {
            result = await this.ogcApi.getBbox(
              { bbox: this.currentGeometry.bbox },
              this.resourceItemData.id,
              undefined,
              serverBaseUrl
            );
          } else {
            throw authErr;
          }
        }
      } else {
        result = await this.ogcApi.getBbox(
          { bbox: this.currentGeometry.bbox },
          this.resourceItemData.id,
          undefined,
          serverBaseUrl
        );
      }

      if (result) {
        this.featureCollection = result;
        this.displayFeaturesOnMap(result.features ?? []);
        if (this.currentGeometry) {
          this.geometryDrawn.emit(this.currentGeometry);
        }
        this.hideDrawControl();
      } else {
        this.applyError = 'Failed to fetch OGC data. The collection may require authentication.';
      }
    } catch (err: any) {
      const msg = err?.error?.detail ?? err?.message ?? err?.statusText ?? 'Failed to fetch OGC data';
      this.applyError = typeof msg === 'string' ? msg : 'Failed to fetch OGC data';
    } finally {
      this.isApplying = false;
      this.cdr.markForCheck();
    }
  }

  getFeaturePropertyKeys(feature: OgcFeature): string[] {
    const p = feature?.properties;
    if (!p || typeof p !== 'object') return [];
    return Object.keys(p);
  }

  /** Download as JSON (feature collection). */
  downloadResults(): void {
    if (!this.featureCollection) return;
    const blob = new Blob([JSON.stringify(this.featureCollection, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ogc-features-${this.resourceItemData?.id ?? 'data'}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    this.toasterService.success('JSON file downloaded successfully');
  }

  /** Download as GPKG (GeoPackage) via collection links – fetches href from getLink and downloads. */
  async downloadFeatures(): Promise<void> {
    const id = this.resourceItemData?.id;
    if (!id) {
      this.toasterService.error('Dataset ID is not available.');
      return;
    }
    try {
      const token = await this.keycloak.getToken().catch(() => undefined);
      const serverBaseUrl = this.resourceItemData?.ogcServerBaseUrl;
      const response = await this.ogcApi.getLink(id, token ?? undefined, serverBaseUrl);
      const links = response?.['links'] as Array<{ href?: string; rel?: string }> | undefined;
      if (!Array.isArray(links) || links.length === 0) {
        this.toasterService.error('No download link available. Downloading as JSON instead.');
        this.downloadResults();
        return;
      }
      // Prefer link with rel="enclosure" (IUDX style), otherwise use last link
      const enclosureLink = links.find((l) => String(l?.rel ?? '').toLowerCase() === 'enclosure');
      const linkToUse = enclosureLink ?? links[links.length - 1];
      const downloadUrl = linkToUse?.href;
      if (!downloadUrl) {
        this.toasterService.error('Download URL is missing. Downloading as JSON instead.');
        this.downloadResults();
        return;
      }
      const blob = await this.ogcApi.fetchBlobFromUrl(downloadUrl, token ?? undefined);
      const resourceLabel = this.resourceItemData?.label ?? this.resourceItemData?.id ?? 'ogc-features';
      const filename = `${String(resourceLabel).split(' ').join('_')}_${new Date().toLocaleDateString().split('/').join('')}.gpkg`;
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/geopackage+sqlite3' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      this.toasterService.success('File downloaded successfully');
    } catch (err) {
      console.error('Error downloading features:', err);
      this.toasterService.error('Failed to download features. Downloading as JSON instead.');
      this.downloadResults();
    }
  }

  closeModal(): void {
    this.closeRequested.emit();
  }

  formatBboxArray(): string {
    if (!this.currentGeometry?.bbox?.length) return '';
    return this.currentGeometry.bbox.map((n) => n.toFixed(6)).join(', ');
  }

  formatPropertiesJson(): string {
    if (!this.selectedFeature?.properties) return '{}';
    return JSON.stringify(this.selectedFeature.properties, null, 2);
  }

  async copyPropertiesJson(): Promise<void> {
    const json = this.formatPropertiesJson();
    try {
      await navigator.clipboard.writeText(json);
      this.copiedToClipboard = true;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.copiedToClipboard = false;
        this.cdr.markForCheck();
      }, 2000);
    } catch {
      this.toasterService.error('Failed to copy to clipboard');
    }
  }

  /** Property entries for enhanced display */
  getPropertyEntries(): { key: string; value: unknown; type: 'email' | 'phone' | 'coord' | 'link' | 'null' | 'text' }[] {
    const p = this.selectedFeature?.properties;
    if (!p || typeof p !== 'object') return [];
    return Object.entries(p).map(([key, value]) => ({
      key,
      value,
      type: this.getPropertyType(key, value),
    }));
  }

  private getPropertyType(key: string, value: unknown): 'email' | 'phone' | 'coord' | 'link' | 'null' | 'text' {
    if (value == null) return 'null';
    const s = String(value).trim().toLowerCase();
    const k = key.toLowerCase();
    if (k.includes('email') && s.includes('@')) return 'email';
    if (k.includes('phone') || k.includes('tel')) return 'phone';
    if (k.includes('lat') || k.includes('lng') || k.includes('longitude')) return 'coord';
    if (s.startsWith('http://') || s.startsWith('https://')) return 'link';
    return 'text';
  }

  formatPropertyValue(value: unknown): string {
    if (value == null) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  clearDrawnShapes(): void {
    this.drawnItems.clearLayers();
    this.currentGeometry = null;
    this.applyError = null;
    this.featureCollection = null;
    this.clearFeatureMarkers();
    this.selectedFeature = null;
    this.cdr.markForCheck();
  }

  /** Reset loaded data and show the drawing toolbar so the user can draw a new selection. */
  resetAndShowToolbar(): void {
    this.clearDrawnShapes();
    this.resetMapView();
    this.addDrawControl();
    this.cdr.markForCheck();
  }

  /** Reset map view to default center and zoom. */
  private resetMapView(): void {
    if (!this.map) return;
    const Lx = L as any;
    this.map.setView(
      Lx.latLng(...OgcFeatureDataComponent.DEFAULT_CENTER),
      OgcFeatureDataComponent.DEFAULT_ZOOM
    );
  }

  closePropertiesPanel(): void {
    this.updateMarkerSelection(this.selectedFeature, false);
    this.selectedFeature = null;
    this.copiedToClipboard = false;
    this.cdr.markForCheck();
  }

  /** Display features on map: Points as clustered markers, Polygon/LineString as GeoJSON layers. */
  private displayFeaturesOnMap(features: OgcFeature[]): void {
    this.clearFeatureMarkers();
    if (!this.map || features.length === 0) return;
    const Lx = L as any;

    for (const feature of features) {
      const geom = feature?.geometry as { type?: string; coordinates?: unknown } | undefined;
      if (!geom) continue;

      const geometryType = geom.type;
      let layer: any;

      try {
        if (geometryType === 'Point' && Array.isArray(geom.coordinates) && geom.coordinates.length >= 2) {
          const [lng, lat] = geom.coordinates as number[];
          layer = Lx.marker([lat, lng], { icon: this.getMarkerIcon() });
          layer.featureData = feature;
          layer.on('click', () => this.selectFeatureInZone(feature));
          this.markerClusterGroup?.addLayer(layer);
          this.featureMarkers.push(layer);
        } else if (geometryType === 'Polygon' || geometryType === 'LineString' || geometryType === 'MultiPoint') {
          layer = Lx.geoJSON(geom, {
            style: {
              color: '#0d5c42',
              weight: 2,
              opacity: 0.8,
              fillOpacity: 0.3,
              fillColor: '#0d5c42',
            },
          });
          (layer as any).featureData = feature;
          layer.on('click', () => this.selectFeatureInZone(feature));
          layer.eachLayer?.((l: any) => {
            l.featureData = feature;
            l.on('click', () => this.selectFeatureInZone(feature));
          });
          this.polygonsLayer.addLayer(layer);
          this.featureMarkers.push(layer);
        } else {
          const coords = this.getFeatureCoordinates(feature);
          if (coords) {
            layer = Lx.marker([coords.lat, coords.lng], { icon: this.getMarkerIcon() });
            layer.featureData = feature;
            layer.on('click', () => this.selectFeatureInZone(feature));
            this.markerClusterGroup?.addLayer(layer);
            this.featureMarkers.push(layer);
          }
        }
      } catch (e) {
        console.warn('Error displaying feature:', e);
      }
    }

    if (this.featureMarkers.length > 0) {
      let bounds: any = null;
      const clusterBounds = this.markerClusterGroup?.getBounds?.();
      const polygonBounds = this.polygonsLayer.getBounds();
      if (clusterBounds?.isValid?.()) bounds = clusterBounds;
      if (polygonBounds?.isValid?.()) {
        bounds = bounds ? bounds.extend(polygonBounds) : polygonBounds;
      }
      if (bounds?.isValid?.()) {
        this.map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }

  private selectFeatureInZone(feature: OgcFeature): void {
    this.ngZone.run(() => {
      this.updateMarkerSelection(this.selectedFeature, false);
      this.selectedFeature = feature;
      this.copiedToClipboard = false;
      this.updateMarkerSelection(feature, true);
      this.cdr.markForCheck();
    });
  }

  private updateMarkerSelection(feature: OgcFeature | null, selected: boolean): void {
    if (!feature) return;
    for (const layer of this.featureMarkers) {
      const fd = (layer as any).featureData;
      if (fd !== feature) continue;
      if (typeof (layer as any).setIcon === 'function') {
        (layer as any).setIcon(this.getMarkerIcon(selected));
      } else if (typeof (layer as any).setStyle === 'function') {
        (layer as any).setStyle({
          color: selected ? '#00d084' : '#0d5c42',
          fillColor: selected ? '#00d084' : '#0d5c42',
        });
      } else {
        (layer as any).eachLayer?.((l: any) => {
          if (l.setStyle) l.setStyle({
            color: selected ? '#00d084' : '#0d5c42',
            fillColor: selected ? '#00d084' : '#0d5c42',
          });
        });
      }
      break;
    }
  }

  private hideDrawControl(): void {
    this.disableDrawMode();
    this.drawControl = null;
    this.cdr.markForCheck();
  }

  private getMarkerIcon(selected = false): L.DivIcon {
    const fill = selected ? '#00d084' : '#0d5c42';
    return (L as any).divIcon({
      className: 'custom-div-icon' + (selected ? ' marker-selected' : ''),
      iconSize: [30, 42],
      iconAnchor: [15, 42],
      html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${fill}" width="30" height="42"><path d="M12 4C9.24 4 7 6.24 7 9c0 2.85 2.92 7.21 5 9.88 2.11-2.69 5-7 5-9.88 0-2.76-2.24-5-5-5zm0 7.5c-1.38 0-2-1.12-2-2s1.12-2 2-2 2 1.12 2 2-1.12 2-2 2z" opacity="1" stroke="white" stroke-width="0.5" /><circle cx="12" cy="9.5" r="2" fill="white"/></svg>`,
    });
  }

  private clearFeatureMarkers(): void {
    this.markerClusterGroup?.clearLayers?.();
    this.polygonsLayer.clearLayers();
    this.featureMarkers = [];
  }

  private getFeatureCoordinates(feature: OgcFeature): { lat: number; lng: number } | null {
    const geom = feature?.geometry as { type?: string; coordinates?: unknown } | undefined;
    if (!geom || !geom.coordinates) return null;
    const coords = geom.coordinates;
    if (geom.type === 'Point' && Array.isArray(coords) && coords.length >= 2) {
      const [lng, lat] = coords as number[];
      if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
    }
    // Polygon: coordinates[0] is ring, [0] is first [lng, lat]. LineString: coordinates[0] is [lng, lat]
    if ((geom.type === 'Polygon' || geom.type === 'LineString') && Array.isArray(coords) && coords.length > 0) {
      const first = coords[0] as number[] | number[][];
      const pair = Array.isArray(first[0]) ? (first as number[][])[0] : (first as number[]);
      if (Array.isArray(pair) && pair.length >= 2) {
        const [lng, lat] = pair;
        if (typeof lat === 'number' && typeof lng === 'number') return { lat, lng };
      }
    }
    return null;
  }
}
